import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface ServicePeriod {
  name: string;
  start_time: string; // HH:mm format
  end_time: string; // HH:mm format
}

export interface ReservationSchedule {
  [dayName: string]: ServicePeriod[];
}

export interface ShiftDateRange {
  start: Date;
  end: Date;
  periodName: string;
}

/**
 * Get the day name for a given date in a specific timezone
 * @param date - The date to get the day name for
 * @param timezone - IANA timezone name
 * @returns Day name in lowercase (e.g., "monday", "tuesday", etc.)
 * Uses hardcoded English day names to ensure consistency regardless of browser locale
 */
export function getDayNameInTimezone(date: Date, timezone: string): string {
  const daysOfWeek = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  const dayIndex = dayjs(date).tz(timezone).day();
  return daysOfWeek[dayIndex];
}

/**
 * Convert a time string (HH:mm) on a specific date and timezone to UTC Date
 * @param dateStr - Date string in YYYY-MM-DD format
 * @param timeStr - Time string in HH:mm format
 * @param timezone - IANA timezone name
 * @returns UTC Date object
 */
export function timeInTimezoneToUTC(
  dateStr: string,
  timeStr: string,
  timezone: string,
): Date {
  const dateTimeStr = `${dateStr} ${timeStr}:00`;
  const dayjsDate = dayjs.tz(dateTimeStr, timezone);
  return dayjsDate.toDate();
}

/**
 * Find the current active shift based on the current time and reservation schedule
 * @param now - Current date/time
 * @param schedule - Reservation schedule by day
 * @param timezone - Restaurant timezone
 * @returns ShiftDateRange for the current shift, or null if no active shift
 */
export function getCurrentShift(
  now: Date,
  schedule: ReservationSchedule | null,
  timezone: string,
): ShiftDateRange | null {
  console.log("[getCurrentShift] Starting with:", {
    now: now.toISOString(),
    schedule,
    timezone,
  });

  if (!schedule) {
    console.log("[getCurrentShift] No schedule provided, returning null");
    return null;
  }

  const nowInTz = dayjs(now).tz(timezone);
  const dayName = getDayNameInTimezone(now, timezone);
  console.log("[getCurrentShift] Day info:", {
    nowInTz: nowInTz.format(),
    dayName,
    scheduleKeys: Object.keys(schedule),
  });

  const daySchedule = schedule[dayName];
  console.log("[getCurrentShift] Day schedule:", { daySchedule });

  if (!daySchedule || !Array.isArray(daySchedule) || daySchedule.length === 0) {
    console.log("[getCurrentShift] No schedule for this day, returning null");
    return null;
  }

  const dateStr = nowInTz.format("YYYY-MM-DD");
  const currentTimeMinutes = nowInTz.hour() * 60 + nowInTz.minute();
  console.log("[getCurrentShift] Current time info:", {
    dateStr,
    currentTimeMinutes,
    currentTimeFormatted: `${nowInTz.hour()}:${nowInTz.minute()}`,
  });

  // Sort periods by start time
  const sortedPeriods = [...daySchedule].sort((a, b) => {
    const [aHour, aMin] = a.start_time.split(":").map(Number);
    const [bHour, bMin] = b.start_time.split(":").map(Number);
    return aHour * 60 + aMin - (bHour * 60 + bMin);
  });
  console.log("[getCurrentShift] Sorted periods:", sortedPeriods);

  // Find the active period
  for (const period of sortedPeriods) {
    console.log(`[getCurrentShift] Checking period: ${period.name} (${period.start_time} - ${period.end_time})`);

    const [startHour, startMin] = period.start_time.split(":").map(Number);
    const [endHour, endMin] = period.end_time.split(":").map(Number);

    const startMinutes = startHour * 60 + startMin;
    let endMinutes = endHour * 60 + endMin;

    // Handle shifts that cross midnight (e.g., 18:00 to 02:00)
    const crossesMidnight = endMinutes <= startMinutes;
    if (crossesMidnight) {
      endMinutes += 24 * 60; // Add 24 hours in minutes
    }

    console.log(`[getCurrentShift] Period check:`, {
      startMinutes,
      endMinutes,
      currentTimeMinutes,
      crossesMidnight,
    });

    // Check if current time is within this period
    // Also handle the case where we're past midnight in a shift that crosses midnight
    const isInPeriod =
      currentTimeMinutes >= startMinutes && currentTimeMinutes < endMinutes;
    const isAfterMidnightInCrossingShift =
      crossesMidnight && currentTimeMinutes < endMinutes - 24 * 60;

    console.log(`[getCurrentShift] Match check:`, {
      isInPeriod,
      isAfterMidnightInCrossingShift,
    });

    if (isInPeriod || isAfterMidnightInCrossingShift) {
      let startDate: Date;
      let endDate: Date;

      if (isAfterMidnightInCrossingShift) {
        // We're past midnight in a shift that started yesterday
        const yesterday = nowInTz.subtract(1, "day").format("YYYY-MM-DD");
        startDate = timeInTimezoneToUTC(yesterday, period.start_time, timezone);
        endDate = timeInTimezoneToUTC(dateStr, period.end_time, timezone);
        console.log(`[getCurrentShift] After midnight crossing shift:`, {
          yesterday,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        });
      } else if (crossesMidnight) {
        // Shift starts today and ends tomorrow
        const tomorrow = nowInTz.add(1, "day").format("YYYY-MM-DD");
        startDate = timeInTimezoneToUTC(dateStr, period.start_time, timezone);
        endDate = timeInTimezoneToUTC(tomorrow, period.end_time, timezone);
        console.log(`[getCurrentShift] Crosses midnight:`, {
          tomorrow,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        });
      } else {
        // Normal shift within the same day
        startDate = timeInTimezoneToUTC(dateStr, period.start_time, timezone);
        endDate = timeInTimezoneToUTC(dateStr, period.end_time, timezone);
        console.log(`[getCurrentShift] Normal shift:`, {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        });
      }

      const result = {
        start: startDate,
        end: endDate,
        periodName: period.name,
      };
      console.log("[getCurrentShift] Found active shift:", result);
      return result;
    }
  }

  console.log("[getCurrentShift] No active shift found, returning null");
  return null;
}

/**
 * Get date filters for the current shift in ISO format (UTC)
 * Suitable for API calls
 * @param now - Current date/time
 * @param schedule - Reservation schedule by day
 * @param timezone - Restaurant timezone
 * @returns Object with date_from and date_to in ISO format
 * If no active shift is found, returns the current calendar day in the restaurant's timezone
 */
export function getCurrentShiftFilters(
  now: Date,
  schedule: ReservationSchedule | null,
  timezone: string,
): { date_from: string; date_to: string } {
  console.log("[getCurrentShiftFilters] Called with:", {
    now: now.toISOString(),
    hasSchedule: !!schedule,
    schedule,
    timezone,
  });

  const shift = getCurrentShift(now, schedule, timezone);
  console.log("[getCurrentShiftFilters] Shift result:", shift);

  if (!shift) {
    console.log("[getCurrentShiftFilters] No shift found, falling back to current day in restaurant timezone");

    // Get current date in restaurant's timezone
    const nowInTz = dayjs(now).tz(timezone);
    const dateStr = nowInTz.format("YYYY-MM-DD");

    // Create start of day (00:00:00) and end of day (23:59:59) in restaurant timezone
    const startOfDay = timeInTimezoneToUTC(dateStr, "00:00", timezone);
    const endOfDay = timeInTimezoneToUTC(dateStr, "23:59", timezone);

    const result = {
      date_from: startOfDay.toISOString(),
      date_to: endOfDay.toISOString(),
    };
    console.log("[getCurrentShiftFilters] Returning current day filters:", result);
    return result;
  }

  const result = {
    date_from: shift.start.toISOString(),
    date_to: shift.end.toISOString(),
  };
  console.log("[getCurrentShiftFilters] Returning shift filters:", result);
  return result;
}
